import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Grid,
  Divider
} from '@mui/material';
import {
  Business,
  LocationOn,
  ContactPhone,
  Numbers,
  Refresh,
  CheckCircle as TickIcon,
  Error as ErrorIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import indexedDBService from '../../services/IndexedDBService';
import { useAuth } from '../../context/AuthContext';

interface BusinessProfileUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  missingFields?: string[];
  userData?: Record<string, any>;
}

const BusinessProfileUpdateDialog: React.FC<BusinessProfileUpdateDialogProps> = ({
  open,
  onClose,
  missingFields = ['name', 'address'],
  userData
}) => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<{ text: string, type: 'success' | 'error' | 'info' } | null>(null);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [businessData, setBusinessData] = useState<Record<string, any> | null>(userData || null);

  // Form state for inline editing
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    gstin: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Fetch business data when dialog opens
  useEffect(() => {
    if (open) {
      fetchBusinessData();
    }
  }, [open, currentUser, userProfile]);

  const fetchBusinessData = async () => {
    try {
      if (!currentUser) {
        return;
      }

      // Try multiple sources for business data
      let businessDetails = await indexedDBService.getBusinessDetails(currentUser.uid);

      // If not found, try to get from all businesses
      if (!businessDetails) {
        const allBusinesses = await indexedDBService.getAllItems('businesses');
        if (allBusinesses && allBusinesses.length > 0) {
          businessDetails = allBusinesses[0];
        }
      }

      // If still not found, use user profile data
      if (!businessDetails && userProfile) {
        businessDetails = {
          id: currentUser.uid,
          name: userProfile.businessName || '',
          businessName: userProfile.businessName || '',
          gstin: userProfile.gstn || '',
          address: userProfile.address || '',
          email: userProfile.email || currentUser.email || '',
          phone: userProfile.phone || '',
          city: userProfile.city || '',
          state: userProfile.state || '',
          pincode: userProfile.pincode || ''
        };
      }

      if (businessDetails) {
        setBusinessData(businessDetails);

        // Update form data
        setFormData({
          name: businessDetails.name || businessDetails.businessName || '',
          address: businessDetails.address || '',
          gstin: businessDetails.gstin || businessDetails.GSTIN || '',
          phone: businessDetails.phone || '',
          email: businessDetails.email || currentUser.email || '',
          city: businessDetails.city || '',
          state: businessDetails.state || '',
          pincode: businessDetails.pincode || ''
        });

        // Determine which fields are available
        const available: string[] = [];
        if (businessDetails.name || businessDetails.businessName) available.push('name');
        if (businessDetails.address) available.push('address');
        if (businessDetails.gstin || businessDetails.GSTIN) available.push('gstin');
        if (businessDetails.phone) available.push('phone');

        setAvailableFields(available);
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
    }
  };

  const handleSaveInline = async () => {
    try {
      setIsSaving(true);

      if (!currentUser) {
        setRefreshMessage({ text: 'You must be logged in to save.', type: 'error' });
        return;
      }

      // Validate required fields
      if (!formData.name || !formData.address) {
        setRefreshMessage({
          text: 'Business Name and Address are required fields.',
          type: 'error'
        });
        return;
      }

      const businessDataToSave = {
        id: currentUser.uid,
        userId: currentUser.uid,
        uid: currentUser.uid,
        name: formData.name,
        businessName: formData.name,
        address: formData.address,
        gstin: formData.gstin,
        GSTIN: formData.gstin,
        phone: formData.phone,
        email: formData.email || currentUser.email,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        updatedAt: new Date().toISOString(),
        createdAt: businessData?.createdAt || new Date().toISOString()
      };

      // Save to IndexedDB
      await indexedDBService.saveBusinessDetails(businessDataToSave);

      // Refresh data
      await fetchBusinessData();

      setRefreshMessage({
        text: 'Business profile saved successfully! You can now create invoices.',
        type: 'success'
      });

      // Close dialog after 1.5 seconds
      setTimeout(() => {
        onClose();
        // Reload the page to refresh invoice creation
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error saving business data:', error);
      setRefreshMessage({ text: 'Error saving business data. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefreshBusinessData = async () => {
    try {
      setIsRefreshing(true);
      setRefreshMessage({ text: 'Refreshing business profile data...', type: 'info' });

      await fetchBusinessData();

      if (formData.name && formData.address) {
        setRefreshMessage({
          text: 'Business profile refreshed successfully!',
          type: 'success'
        });
      } else {
        setRefreshMessage({
          text: 'No business data found. Please fill in the form below.',
          type: 'info'
        });
      }
    } catch (error) {
      console.error('Error refreshing business data:', error);
      setRefreshMessage({ text: 'Error refreshing business data.', type: 'error' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={600}>
            Complete Business Profile
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          To create invoices, you need to complete your business profile first.
        </Alert>

        {refreshMessage && (
          <Alert severity={refreshMessage.type} sx={{ mb: 3, borderRadius: 2 }}>
            {refreshMessage.text}
          </Alert>
        )}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Fill in your business information below or click refresh if you've already set it up elsewhere.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Business Name *"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="Enter your business name"
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number *"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              placeholder="Enter phone number"
              InputProps={{
                startAdornment: <ContactPhone sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Business Address *"
              value={formData.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              placeholder="Enter your business address"
              multiline
              rows={2}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleFormChange('city', e.target.value)}
              placeholder="City"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => handleFormChange('state', e.target.value)}
              placeholder="State"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Pincode"
              value={formData.pincode}
              onChange={(e) => handleFormChange('pincode', e.target.value)}
              placeholder="Pincode"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="GSTIN (Optional)"
              value={formData.gstin}
              onChange={(e) => handleFormChange('gstin', e.target.value)}
              placeholder="Enter GSTIN if applicable"
              InputProps={{
                startAdornment: <Numbers sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            startIcon={isRefreshing ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRefreshBusinessData}
            disabled={isRefreshing}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderWidth: 2,
              textTransform: 'none',
              '&:hover': {
                borderWidth: 2
              }
            }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 3,
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveInline}
          variant="contained"
          disabled={isSaving || !formData.name || !formData.address}
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
            textTransform: 'none',
            px: 3,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.5)'
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessProfileUpdateDialog; 
